class CustomizeCanvas {
  constructor(canvasElement, baseImageUrl) {
    this.canvas = canvasElement;
    this.ctx = this.canvas.getContext('2d');
    this.baseImageUrl = baseImageUrl;
    this.baseImage = null;
    this.patternImage = null;
    this.currentState = {
      pattern: null,
      color: null,
      text: null
    };
    this.imagesLoaded = 0;
    this.totalImages = 1;
    this.onLoadCallback = null;

    this.init();
  }

  async init() {
    await this.loadBaseImage();
    this.setupCanvasSize();
  }

  setupCanvasSize() {
    const wrapper = this.canvas.closest('.product-customize-canvas-wrapper');
    if (!wrapper) return;

    const containerWidth = wrapper.clientWidth;
    const containerHeight = wrapper.clientHeight;
    const isMobile = window.innerWidth < 1024;

    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = containerWidth * dpr;
    this.canvas.height = containerHeight * dpr;
    this.ctx.scale(dpr, dpr);

    const isMokoPin = this.canvas.closest('product-customize-modal')?.dataset.hasMokoPin === 'true';

    if (isMobile && !isMokoPin) {
      this.canvas.style.width = `120%`;
      this.canvas.style.height = `auto`;
    } else {
      this.canvas.style.width = `${containerWidth}px`;
      this.canvas.style.height = `auto`;
    }

    this.render();
  }

  loadBaseImage() {
    return new Promise((resolve, reject) => {
      if (!this.baseImageUrl) {
        reject(new Error('No base image URL provided'));
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';

      const timeout = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        console.warn('[CustomizeCanvas] Image load timeout');
        this.baseImage = null;
        resolve();
      }, 10_000);

      img.onload = () => {
        clearTimeout(timeout);
        this.baseImage = img;
        this.render();
        resolve();
      };

      img.onerror = () => {
        clearTimeout(timeout);
        console.warn('[CustomizeCanvas] Failed to load base image:', this.baseImageUrl);
        this.baseImage = null;
        resolve();
      };

      img.src = this.baseImageUrl;
    });
  }

  loadPatternImage(imageUrl) {
    return new Promise((resolve, reject) => {
      if (!imageUrl) {
        this.patternImage = null;
        this.render();
        resolve();
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';

      const timeout = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        console.warn('[CustomizeCanvas] Pattern image load timeout');
        this.patternImage = null;
        this.render();
        resolve();
      }, 5_000);

      img.onload = () => {
        clearTimeout(timeout);
        this.patternImage = img;
        this.render();
        resolve();
      };

      img.onerror = () => {
        clearTimeout(timeout);
        console.warn('[CustomizeCanvas] Failed to load pattern image:', imageUrl);
        this.patternImage = null;
        this.render();
        resolve();
      };

      img.src = imageUrl;
    });
  }

  render() {
    if (!this.baseImage) return;

    const width = this.canvas.width / (window.devicePixelRatio || 1);
    const height = this.canvas.height / (window.devicePixelRatio || 1);

    this.ctx.clearRect(0, 0, width, height);

    this.drawBaseImage(width, height);

    if (this.currentState.color) {
      this.applyColorOverlay(width, height);
    }

    if (this.currentState.pattern && this.patternImage) {
      this.applyPatternOverlay(width, height);
    }

    if (this.currentState.text && this.currentState.text.content) {
      this.drawText(width, height);
    }
  }

  drawBaseImage(width, height) {
    if (!this.baseImage) return;

    const imgAspect = this.baseImage.width / this.baseImage.height;
    const canvasAspect = width / height;

    let drawWidth, drawHeight, drawX, drawY;

    if (imgAspect > canvasAspect) {
      drawWidth = width;
      drawHeight = drawWidth / imgAspect;
      drawX = 0;
      drawY = (height - drawHeight) / 2;
    } else {
      drawHeight = height;
      drawWidth = drawHeight * imgAspect;
      drawX = (width - drawWidth) / 2;
      drawY = 0;
    }

    this.ctx.drawImage(this.baseImage, drawX, drawY, drawWidth, drawHeight);

    this.drawArea = { x: drawX, y: drawY, width: drawWidth, height: drawHeight };
  }

  applyColorOverlay(width, height) {
    if (!this.currentState.color || !this.currentState.color.hex) return;

    const hex = this.currentState.color.hex;
    const rgb = this.hexToRgb(hex);
    if (!rgb) return;

    this.ctx.globalCompositeOperation = 'multiply';
    this.ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`;
    this.ctx.fillRect(0, 0, width, height);
    this.ctx.globalCompositeOperation = 'source-over';
  }

  applyPatternOverlay(width, height) {
    if (!this.patternImage || !this.drawArea) return;

    this.ctx.globalCompositeOperation = 'overlay';
    this.ctx.globalAlpha = 0.5;

    const pattern = this.ctx.createPattern(this.patternImage, 'repeat');
    this.ctx.fillStyle = pattern;
    this.ctx.fillRect(this.drawArea.x, this.drawArea.y, this.drawArea.width, this.drawArea.height);

    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.globalAlpha = 1.0;
  }

  drawText(width, height) {
    if (!this.currentState.text || !this.currentState.text.content || !this.drawArea) return;

    const text = this.currentState.text.content;
    let fontSize = this.currentState.text.fontSize || 8;
    const fontFamily = this.currentState.text.fontFamily || 'Arial, sans-serif';
    const fontWeight = this.currentState.text.fontWeight || 'bold';
    const position = this.currentState.text.position || { x: 0.5, y: 0.5 };

    // const isMobile = window.innerWidth < 1024;

    const textX = this.drawArea.x + (this.drawArea.width * position.x);
    const textY = this.drawArea.y + (this.drawArea.height * position.y);

    this.ctx.save();

    this.ctx.translate(textX, textY);
    this.ctx.rotate(-Math.PI / 2);

    this.ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    this.ctx.fillStyle = '#FFFFFF';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    this.ctx.fillText(text, 0, 0);

    this.ctx.restore();
  }

  setPattern(pattern) {
    this.currentState.pattern = pattern;
    if (pattern?.imageUrl) {
      return this.loadPatternImage(pattern.imageUrl);
    }
    this.patternImage = null;
    this.render();
    return Promise.resolve();
  }

  setColor(color) {
    this.currentState.color = color;
    this.render();
  }

  setText(text) {
    this.currentState.text = text;
    this.render();
  }

  getState() {
    return { ...this.currentState };
  }

  clear() {
    this.currentState = {
      pattern: null,
      color: null,
      text: null
    };
    this.patternImage = null;
    this.render();
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  handleResize() {
    this.setupCanvasSize();
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomizeCanvas;
}

